import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401 && await refreshSession()) {
        return;
      }
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        email,
        password
      });
      
      const { access_token, refresh_token, user: userData } = response.data;
      localStorage.setItem('adminToken', access_token);
      if (refresh_token) localStorage.setItem('adminRefreshToken', refresh_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const refreshSession = async () => {
    const rt = localStorage.getItem('adminRefreshToken');
    if (!rt) return false;
    try {
      const r = await axios.post(`${API_URL}/api/admin/refresh`, { refresh_token: rt });
      localStorage.setItem('adminToken', r.data.access_token);
      if (r.data.refresh_token) localStorage.setItem('adminRefreshToken', r.data.refresh_token);
      setToken(r.data.access_token);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!token) return;
    const interval = setInterval(refreshSession, 40 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
